/**
 * Geofence Service для города Алтай
 * Проверяет нахождение пользователя в радиусе 5км от центра города
 */

// Координаты центра города Алтай
const ALTAI_CENTER = {
  lat: 49.721168,
  lng: 84.270336
};

// Радиус геозоны в метрах (5 км)
const GEOFENCE_RADIUS = 5000;

/**
 * Вычисляет расстояние между двумя координатами в метрах
 * Использует формулу Haversine
 */
function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371e3; // радиус Земли в метрах
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lng2 - lng1) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
}

/**
 * Проверяет, находится ли пользователь в геозоне города Алтай
 */
export function isWithinGeofence(userLat, userLng) {
  const distance = calculateDistance(
    ALTAI_CENTER.lat,
    ALTAI_CENTER.lng,
    userLat,
    userLng
  );
  
  console.log(`Расстояние до центра Алтая: ${Math.round(distance)}м`);
  return distance <= GEOFENCE_RADIUS;
}

/**
 * Получает текущую геопозицию пользователя
 */
export function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const err = new Error('Геолокация не поддерживается этим браузером');
      err.geoCode = 'unsupported';
      reject(err);
      return;
    }

    const options = {
      enableHighAccuracy: false, // Для мобильных устройств
      timeout: 30000, // Увеличиваем до 30 секунд
      maximumAge: 60000 // 1 минута кэша
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        });
      },
      (error) => {
        let errorMessage = 'Не удалось получить геолокацию';
        let geoCode = 'generic';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Доступ к геолокации запрещен';
            geoCode = 'denied';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Геолокация недоступна';
            geoCode = 'unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Превышено время ожидания геолокации';
            geoCode = 'timeout';
            break;
        }

        const err = new Error(errorMessage);
        err.geoCode = geoCode;
        reject(err);
      },
      options
    );
  });
}

/**
 * Проверяет геозону и возвращает результат
 */
export async function checkGeofenceAccess() {
  try {
    const position = await getCurrentPosition();
    const isInside = isWithinGeofence(position.lat, position.lng);
    
    return {
      success: true,
      isInside: isInside,
      position: position,
      distance: Math.round(calculateDistance(
        ALTAI_CENTER.lat,
        ALTAI_CENTER.lng,
        position.lat,
        position.lng
      ))
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
      errorCode: error.geoCode || 'generic'
    };
  }
}

const GEOFENCE_MESSAGES = {
  kk: {
    errorTitle: 'Геолокация қатесі',
    errorDetails: 'Қолданбаға кіру үшін геолокацияға рұқсат беру қажет',
    welcomeTitle: 'Қош келдіңіз!',
    welcomeMessage: 'Сіз Алтай қаласындасыз',
    welcomeDetails: (d) => `Орталыққа дейінгі қашықтық: ${d}м`,
    blockedTitle: 'Қолжетімділік шектелген',
    blockedMessage: 'Кіру тек Алтай қаласының тұрғындары үшін қолжетімді',
    blockedDetails: (d) => `Сіз қаладан ${d}м қашықтықтасыз (максимум: 5км)`,
    geoErrors: {
      unsupported: 'Бұл браузер геолокацияны қолдамайды',
      denied: 'Геолокацияға рұқсат берілмеген',
      unavailable: 'Геолокация қолжетімсіз',
      timeout: 'Геолокацияны күту уақыты асып кетті',
      generic: 'Геолокацияны алу мүмкін болмады'
    }
  },
  ru: {
    errorTitle: 'Ошибка геолокации',
    errorDetails: 'Для входа в приложение необходимо разрешить доступ к геолокации',
    welcomeTitle: 'Добро пожаловать!',
    welcomeMessage: 'Вы находитесь в городе Алтай',
    welcomeDetails: (d) => `Расстояние до центра: ${d}м`,
    blockedTitle: 'Доступ ограничен',
    blockedMessage: 'Вход доступен только для жителей города Алтай',
    blockedDetails: (d) => `Вы находитесь в ${d}м от города (максимум: 5км)`,
    geoErrors: {
      unsupported: 'Геолокация не поддерживается этим браузером',
      denied: 'Доступ к геолокации запрещен',
      unavailable: 'Геолокация недоступна',
      timeout: 'Превышено время ожидания геолокации',
      generic: 'Не удалось получить геолокацию'
    }
  }
};

/**
 * Форматирует сообщение о статусе геозоны на выбранном языке
 */
export function getGeofenceMessage(result, language = 'ru') {
  const m = GEOFENCE_MESSAGES[language] || GEOFENCE_MESSAGES.ru;

  if (!result.success) {
    return {
      type: 'error',
      title: m.errorTitle,
      message: m.geoErrors[result.errorCode] || result.error,
      details: m.errorDetails
    };
  }

  if (result.isInside) {
    return {
      type: 'success',
      title: m.welcomeTitle,
      message: m.welcomeMessage,
      details: m.welcomeDetails(result.distance)
    };
  } else {
    return {
      type: 'blocked',
      title: m.blockedTitle,
      message: m.blockedMessage,
      details: m.blockedDetails(result.distance)
    };
  }
}

export default {
  isWithinGeofence,
  getCurrentPosition,
  checkGeofenceAccess,
  getGeofenceMessage,
  ALTAI_CENTER,
  GEOFENCE_RADIUS
};